/**
 * Reviewer handoff / consensus queue and ICU bedside escalation helpers.
 */

export const CONSENSUS_QUEUE_SCHEMA_VERSION = "consensus-queue-v1.0.0";
export const ICU_ESCALATION_SCHEMA_VERSION = "icu-escalation-v1.0.0";

export type ConsensusQueueState =
  | "unassigned"
  | "first_reader"
  | "awaiting_second_reader"
  | "in_disagreement"
  | "resolved"
  | "signed";

export type ConsensusRationaleEntry = {
  atIso: string;
  reviewerId: string;
  decision: "agree" | "disagree" | "escalate" | "resolve";
  rationale: string;
  signed: boolean;
};

export type ConsensusQueueItem = {
  studyId: string;
  reportId?: string | null;
  state: ConsensusQueueState;
  assigneeId: string | null;
  secondReaderId: string | null;
  rationaleHistory: ConsensusRationaleEntry[];
  schemaVersion: string;
};

export function createConsensusQueueItem(input: {
  studyId: string;
  reportId?: string | null;
  assigneeId?: string | null;
}): ConsensusQueueItem {
  return {
    studyId: input.studyId,
    reportId: input.reportId ?? null,
    state: input.assigneeId ? "first_reader" : "unassigned",
    assigneeId: input.assigneeId ?? null,
    secondReaderId: null,
    rationaleHistory: [],
    schemaVersion: CONSENSUS_QUEUE_SCHEMA_VERSION,
  };
}

export function routeToSecondReader(
  item: ConsensusQueueItem,
  secondReaderId: string,
): ConsensusQueueItem {
  return {
    ...item,
    secondReaderId,
    state: "awaiting_second_reader",
  };
}

export function appendConsensusRationale(
  item: ConsensusQueueItem,
  entry: Omit<ConsensusRationaleEntry, "atIso"> & { at?: Date },
): ConsensusQueueItem {
  const next: ConsensusRationaleEntry = {
    atIso: (entry.at || new Date()).toISOString(),
    reviewerId: entry.reviewerId,
    decision: entry.decision,
    rationale: entry.rationale,
    signed: entry.signed,
  };
  let state = item.state;
  if (entry.decision === "disagree") state = "in_disagreement";
  if (entry.decision === "resolve") state = "resolved";
  if (entry.decision === "agree" && entry.signed) state = "signed";
  return {
    ...item,
    state,
    rationaleHistory: [...item.rationaleHistory, next],
  };
}

export type IcuEscalationKind =
  | "notify_expert"
  | "qeeg_alert_confirmation"
  | "therapy_change_checkpoint"
  | "bedside_mode";

export type IcuEscalationEvent = {
  eventId: string;
  studyId: string;
  kind: IcuEscalationKind;
  requiresConfirmation: boolean;
  confirmed: boolean;
  confirmedBy: string | null;
  message: string;
  atIso: string;
  schemaVersion: string;
};

export function createIcuEscalationEvent(input: {
  eventId: string;
  studyId: string;
  kind: IcuEscalationKind;
  message: string;
  requiresConfirmation?: boolean;
  at?: Date;
}): IcuEscalationEvent {
  const requiresConfirmation =
    input.requiresConfirmation ??
    (input.kind === "qeeg_alert_confirmation" ||
      input.kind === "therapy_change_checkpoint");
  return {
    eventId: input.eventId,
    studyId: input.studyId,
    kind: input.kind,
    requiresConfirmation,
    confirmed: false,
    confirmedBy: null,
    message: input.message,
    atIso: (input.at || new Date()).toISOString(),
    schemaVersion: ICU_ESCALATION_SCHEMA_VERSION,
  };
}

export function confirmIcuEscalation(
  event: IcuEscalationEvent,
  confirmedBy: string,
): IcuEscalationEvent {
  return {
    ...event,
    confirmed: true,
    confirmedBy,
  };
}

export function canTakeReportImpactingAction(
  pendingAlerts: IcuEscalationEvent[],
): boolean {
  return !pendingAlerts.some(
    (event) =>
      event.kind === "qeeg_alert_confirmation" &&
      event.requiresConfirmation &&
      !event.confirmed,
  );
}
